import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  listSubmissions,
  type AccountSubmission,
} from "@/lib/brokerSubmissions";
import { messageFor } from "@/lib/errors";
import {
  formatListDate,
  statusBadgeClass,
  statusLabel,
} from "@/lib/listFormat";

// The brokerage's own submissions (BRK-S034) — the portal's working home for the
// quoting flow. Each row links into the submission detail + quoting view
// (BRK-S040). Own-scoped server-side; the SPA only calls /api/broker/**.
//
// The toolbar (search + status/coverage/date filters), the row Actions menu, and
// the pager (page controls + per-page) are PRESENTATIONAL scaffolding only: they
// render to match the intended layout but do not filter/paginate yet — the list
// still loads the first page of the real submissions and the row links work.
const PAGE_SIZE = 25;

export function SubmissionsPage() {
  const { authedRequest } = useAuth();
  const [rows, setRows] = useState<AccountSubmission[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    setRows(null);
    (async () => {
      try {
        const page = await listSubmissions(authedRequest, { limit: PAGE_SIZE });
        if (!cancelled) setRows(page.items);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  useEffect(() => load(), [load]);

  return (
    <section className="flex w-full flex-col gap-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Submissions</h1>
          <p className="text-brk-dim">Track your submissions to market.</p>
        </div>
        <Link
          to="/submissions/new"
          className="inline-flex items-center gap-2 rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong"
        >
          <span aria-hidden="true" className="text-base leading-none">
            +
          </span>
          New submission
        </Link>
      </div>

      {/* Toolbar — presentational only (does not filter yet). */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brk-dim"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="m20 20-3.2-3.2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <input
            type="search"
            aria-label="Search submissions"
            placeholder="Search submissions…"
            className="w-full rounded-md border border-brk-border bg-brk-panel py-2 pl-9 pr-3 text-sm text-brk-text placeholder:text-brk-dim focus:border-brk-accent focus:outline-none focus:ring-2 focus:ring-brk-accent/30"
          />
        </div>
        <div className="relative">
          <select
            aria-label="Status"
            defaultValue=""
            className="appearance-none rounded-md border border-brk-border bg-brk-panel py-2 pl-3 pr-8 text-sm text-brk-text transition-colors hover:bg-brk-bg focus:border-brk-accent focus:outline-none focus:ring-2 focus:ring-brk-accent/30"
          >
            <option value="">All statuses</option>
            <option value="quoted">Quoted</option>
            <option value="declined">Declined</option>
            <option value="waiting_on_carrier">Waiting on carrier</option>
          </select>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-brk-dim"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </div>
        <FilterButton label="All coverages" />
        <FilterButton label="Date range" calendar />
        <button
          type="button"
          aria-label="More filters"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brk-border bg-brk-panel text-brk-dim transition-colors hover:bg-brk-bg"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
        </button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {rows === null && !error && (
        <p role="status" className="text-sm text-brk-dim">
          Loading submissions…
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
          <p className="text-sm text-brk-text">No submissions yet.</p>
          <Link
            to="/submissions/new"
            className="text-sm font-medium text-brk-accent hover:underline"
          >
            Start your first submission →
          </Link>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-brk-border bg-brk-panel">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem] text-left text-sm">
              <thead className="border-b border-brk-border text-xs font-semibold uppercase tracking-wide text-brk-dim">
                <tr>
                  <SortHeader>Submission</SortHeader>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-4 py-3">Coverage</th>
                  <SortHeader>Created</SortHeader>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Carrier</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.id}
                    className="border-t border-brk-border transition-colors hover:bg-brk-bg/60"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/submissions/${s.id}`}
                        className="font-mono font-semibold text-brk-accent hover:underline"
                      >
                        {s.submissionNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-medium text-brk-text">
                      {s.accountLegalName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-brk-dim">{s.coverage}</td>
                    <td className="px-4 py-3 text-brk-dim">
                      {formatListDate(s.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                          displayStatus(s.status),
                        )}`}
                      >
                        {statusLabel(displayStatus(s.status))}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-brk-dim">
                      {s.carrier ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* Row actions — presentational only (no menu wired yet). */}
                      <button
                        type="button"
                        aria-label={`Actions for ${s.submissionNumber}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brk-dim transition-colors hover:bg-brk-bg hover:text-brk-text"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <circle cx="5" cy="12" r="1.6" />
                          <circle cx="12" cy="12" r="1.6" />
                          <circle cx="19" cy="12" r="1.6" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pager — presentational only (single page of results today). */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brk-border px-4 py-3 text-sm text-brk-dim">
            <span>
              Showing 1 to {rows.length} of {rows.length} submissions
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brk-border text-brk-dim opacity-50"
                >
                  ‹
                </span>
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brk-accent font-medium text-brk-accent">
                  1
                </span>
                <span
                  aria-hidden="true"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brk-border text-brk-dim opacity-50"
                >
                  ›
                </span>
              </div>
              <FilterButton label="10 per page" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// A dropdown-styled button used across the toolbar + pager. Presentational only.
function FilterButton({
  label,
  calendar,
}: {
  label: string;
  calendar?: boolean;
}) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-sm text-brk-text transition-colors hover:bg-brk-bg"
    >
      {calendar && (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
      )}
      {label}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="text-brk-dim"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  );
}

// A sortable-looking column header (the caret is presentational). The label
// keeps its meaning; sorting isn't wired.
function SortHeader({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3">
      <span className="inline-flex items-center gap-1">
        {children}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="text-brk-dim/70"
        >
          <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
        </svg>
      </span>
    </th>
  );
}

// On this page every submission reads as either "In Progress" (still being
// worked) or "Waiting on Carrier" (out to market) — never bound/quoted/draft.
// Display-only: the underlying submission status is unchanged.
function displayStatus(status: string): string {
  return status === "draft" ? "in_progress" : "waiting_on_carrier";
}
